import { Injectable, signal, WritableSignal } from '@angular/core';

/** Possible severity levels for the toast notifications */
export type ToastSeverity = 'success' | 'info' | 'warn' | 'error';

/** Corner or edge the toast stack is anchored to */
export type ToastPositionType = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

/** Advanced options for displaying a toast notification */
export interface IToastAdvancedOptions {
  /** Custom buttons inside the toast, each with text and an associated action (max. 3) */
  buttons?: IToastAdvancedOptionsButton[];
}

/** A single action button inside a toast notification */
export interface IToastAdvancedOptionsButton {
  /** Label of the button */
  text: string;
  /** Callback invoked when the button is activated */
  action: () => void;
}

/** A single toast notification currently on screen */
export interface IToast {
  /** Unique id used to address the toast when closing it */
  id: number;
  /** Title of the notification */
  summary: string;
  /** Message body of the notification */
  text: string;
  /** Severity level, drives colors, icon and screen reader politeness */
  severity: ToastSeverity;
  /** Display duration in milliseconds, `0` keeps the toast until it is closed manually */
  life: number;
  /** Custom buttons rendered below the message */
  buttons?: IToastAdvancedOptionsButton[];
}

/** Auto-close bookkeeping for a single toast */
interface IToastTimer {
  /** Handle of the running timeout */
  handle: ReturnType<typeof setTimeout>;
  /** Milliseconds left when the timer was last started */
  remaining: number;
  /** Timestamp the timer was last started at */
  startedAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  // Properties & Signals
  /** The position of the toast notifications */
  readonly position: WritableSignal<ToastPositionType> = signal('bottom-left');
  /** The toasts currently on screen, oldest first */
  readonly toasts: WritableSignal<IToast[]> = signal<IToast[]>([]);

  /** Source for unique toast ids */
  private nextId = 0;
  /** Running auto-close timers, keyed by toast id */
  private readonly timers = new Map<number, IToastTimer>();
  /** Remaining lifetime of paused toasts, keyed by toast id */
  private readonly pausedRemaining = new Map<number, number>();

  // Methods
  /**
   * Displays a toast notification with the specified summary, severity, and duration.
   * @param summary - The title to display in the toast.
   * @param text - The message to display in the toast.
   * @param severity - The severity level of the toast, which can be 'success', 'info', 'warn' or 'error'.
   * @param life - The duration (in milliseconds) for which the toast should be visible, `0` disables auto-close.
   * @param advancedOptions - Optional extras such as custom action buttons.
   */
  showToast(summary = '', text = '', severity: ToastSeverity = 'info', life = 3000, advancedOptions?: IToastAdvancedOptions): void {
    const toast: IToast = { id: this.nextId++, summary, text, severity, life, buttons: advancedOptions?.buttons };
    this.toasts.update((toasts) => [...toasts, toast]);
    this.startTimer(toast.id, toast.life);
  }

  /**
   * Removes a toast from the screen and clears its auto-close timer.
   * @param id - Id of the toast to remove.
   */
  dismiss(id: number): void {
    this.clearTimer(id);
    this.toasts.update((toasts) => toasts.filter((toast) => toast.id !== id));
  }

  /** Removes all toasts from the screen. */
  clear(): void {
    this.timers.forEach((timer) => clearTimeout(timer.handle));
    this.timers.clear();
    this.pausedRemaining.clear();
    this.toasts.set([]);
  }

  /**
   * Halts the auto-close countdown while the user reads or interacts with the toast (WCAG 2.2.1).
   * @param id - Id of the toast to pause.
   */
  pause(id: number): void {
    const timer = this.timers.get(id);
    if (!timer) return;
    clearTimeout(timer.handle);
    timer.remaining = Math.max(0, timer.remaining - (Date.now() - timer.startedAt));
    this.timers.delete(id);
    this.pausedRemaining.set(id, timer.remaining);
  }

  /**
   * Resumes the auto-close countdown with the time that was left when it was paused.
   * Closes the toast right away if its lifetime already ran out while it was paused.
   * @param id - Id of the toast to resume.
   */
  resume(id: number): void {
    const remaining = this.pausedRemaining.get(id);
    if (remaining === undefined) return;
    this.pausedRemaining.delete(id);
    if (remaining <= 0) {
      this.dismiss(id);
      return;
    }
    this.startTimer(id, remaining);
  }

  /**
   * Starts the auto-close countdown for a toast.
   * @param id - Id of the toast to close when the time is up.
   * @param life - Milliseconds until the toast closes, `0` disables auto-close.
   */
  private startTimer(id: number, life: number): void {
    if (life <= 0) return;
    const handle = setTimeout(() => this.dismiss(id), life);
    this.timers.set(id, { handle, remaining: life, startedAt: Date.now() });
  }

  /**
   * Stops and forgets the auto-close countdown of a toast.
   * @param id - Id of the toast whose timer should be cleared.
   */
  private clearTimer(id: number): void {
    const timer = this.timers.get(id);
    if (timer) clearTimeout(timer.handle);
    this.timers.delete(id);
    this.pausedRemaining.delete(id);
  }
}
